import {useTranslation} from 'react-i18next';
import ModalComponent from '@components/modal/Modal.component';
import {Colors} from '@constants/colors.constants';
import React, {useEffect, useState} from 'react';
import {useSnackbar} from 'notistack';
import {Box, Button, Divider, Stack, Typography} from '@mui/material';
import COLUMN_TYPES from '@utils/table/columnTypes.table';
import {
  GridCellParams,
  GridValueFormatterParams,
  GridValueGetterParams,
  GridValueSetterParams
} from '@mui/x-data-grid-pro';
import {
  ExecutionLine,
  executionLineQuery,
  executionLineService,
  ExecutionLineState,
  searchExecutionLinesEffect
} from '@store/invoices/executionLines';
import {NumberExt} from '@utils/extensions/number.extension';
import {Alert, Comment} from '@icons/index';
import {Execution} from '@store/operations/executions';
import NewTableComponent from '@components/table/NewTable.component';
import {useEffectFn} from '@ngneat/effects-hooks';
import {useObservable} from '@ngneat/react-rxjs';
import DateRangePickerComponent from '@components/inputs/dateRangePicker/DateRangePicker.component';
import SearchComponent from '@components/inputs/search/Search.component';
import {emitOnce} from '@ngneat/elf';
import SelectComponent from '@components/inputs/select/Select.component';
import TableFiltersComponent from '@components/table/filters/TableFilters.component';
import {FilterBaseEntityEnum} from '@store/filters';
import {checkIfErrors, FieldValidationType, getFieldError} from '@utils/yup.utils';
import {InvoiceCreation, invoicesQuery, invoicesService} from '@store/invoices/invoices';
import {FacturationRuleType} from '@store/clients/details/client-details.enums';
import DatePickerComponent from '@components/inputs/datePicker/DatePicker.component';
import {InvoicingSource} from '@store/common/organisation.model';
import CheckboxComponent from '@components/inputs/checkbox/Checkbox.component';
import {AdminRoutes, InvoicesRoutes} from '@utils/routes.utils';
import {useNavigate} from 'react-router-dom';
import {LoadingButton} from '@mui/lab';

interface GenerateBillFromExecLinesModalProps {
  handleClose?: () => void;
}

const GenerateBillFromExecLinesModal = (props: GenerateBillFromExecLinesModalProps) => {
  const {handleClose} = props;
  const {t} = useTranslation();

  const {enqueueSnackbar} = useSnackbar();
  const navigate = useNavigate();

  const searchExecutions = useEffectFn(searchExecutionLinesEffect);

  const [step, setStep] = useState(0);

  const [{ executionLines, loading }] = useObservable(executionLineQuery.executionLines$);
  const [selectedExecutionLines] = useObservable(executionLineQuery.selectedExecutionLines$);
  const [{loading: generateLoading}] = useObservable(invoicesQuery.postBill$);

  const [filters] = useObservable(executionLineQuery.filters$);
  const [sortModel] = useObservable(executionLineQuery.sort$);
  const [tablePagination] = useObservable(executionLineQuery.pagination$);

  const [bill, setBill] = useState<Partial<InvoiceCreation>>({documentDate: new Date(), reminderDate: new Date(), invoicingRule: FacturationRuleType.CLIENT_UNIT_DISTINCT});

  const updateBill = (name: string) => (value: any) => setBill((state) => ({...state, [name]: value}));

  const handleGenerate = () => {
    const selectedIds = selectedExecutionLines as any;

    invoicesService.postInvoiceFromLines(selectedIds, bill)
      .subscribe({
        next: (res: { jobInvoiceId: number; error: any }) => {
          if (res.error) {
            enqueueSnackbar(t('invoices.bills.create.error'), {variant: 'error'});
            return;
          }

          enqueueSnackbar(t('invoices.bills.create.success'), {variant: 'success'});
          navigate(`/${AdminRoutes.INVOICES}/${InvoicesRoutes.INVOICES_VALIDATION}/${res.jobInvoiceId}`);
        },
        error: (err) => enqueueSnackbar(err.text, err.options)
      });
  };

  useEffect(() => {
    executionLineService.resetFilters();
    executionLineService.setFilters('invoicingSource')(InvoicingSource.MOS);
  }, []);

  useEffect(() => {
    searchExecutions({ filters, tablePagination, sort: sortModel });
  }, [filters, searchExecutions, sortModel, tablePagination]);

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Stack pt="20px" pb="20px" height="85vh" width="100%" direction="column" spacing={2} alignItems="center">
            <Typography>
              {t('clients.details.contractedServices.updatePrices.step1Description')}
            </Typography>
            <Stack flex={1} width="100%" spacing={2}>
              <Stack direction="row" justifyContent="space-between">
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box>
                    <DateRangePickerComponent
                      size="small"
                      inputWidth={140}
                      placeholderStart={t('invoices.executions.filters.from')}
                      placeholderEnd={t('invoices.executions.filters.to')}
                      startDate={filters.from}
                      endDate={filters.to}
                      color="primary" 
                      handleChangeStartDate={executionLineService.setFilters('from')}
                      handleChangeEndDate={executionLineService.setFilters('to')} />
                  </Box>
                </Stack>
                <Stack direction="column" alignItems="flex-end" >
                  <Box>
                    <CheckboxComponent
                      handleChange={(value) => {
                        console.log(selectedExecutionLines);
                        if (value) {
                          executionLineService.setFilters('ids')(selectedExecutionLines);
                        } else {
                          executionLineService.setFilters('ids')([]);
                        }

                      }}
                      value={(filters?.ids?.length || 0) > 0}
                      label={t('invoices.executions.filters.filterOnlySelected')}
                    />
                  </Box>
                </Stack>
              </Stack>
              <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box width="500px">
                    <SearchComponent
                      value={filters.search}
                      handleChange={(value) => {
                        emitOnce(() => {
                          executionLineService.setFilters('search')(value);
                          executionLineService.setPagination('page')(1);
                        });
                      }}
                    />
                  </Box><Box>
                  <SelectComponent
                    inputWidth={300}
                    color="secondary"
                    items={ExecutionLineState.selectItems()}
                    value={filters.state}
                    handleChange={executionLineService.setFilters('state')}
                    placeholder={t('invoices.executions.filters.state')}
                  />
                </Box>
                </Stack>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Button onClick={() => executionLineService.setSelectedExecutionLines([])} variant="outlined" color="primary" size="small">
                    {t('global.resetSelection')}
                  </Button>
                  <Divider variant="fullWidth" orientation="vertical" sx={{ height: '30px' }} />
                  <TableFiltersComponent
                    baseEntity={FilterBaseEntityEnum.EXECUTION_LINE}
                    filters={filters}
                    handleSave={executionLineService.setFilters('tableFilters')}
                    handleReset={() => executionLineService.resetFilters()} />
                </Stack>

              </Stack>
              <Stack flex={1}>
              <NewTableComponent
                columnThreshold={0}
                loading={loading}
                columns={[
                  COLUMN_TYPES.TEXT({
                    field: 'execution.executedAt',
                    cellClassName: (params: GridCellParams<any, ExecutionLine>) =>
                      params.row.executedAt ? 'success' : 'secondary',
                    label: t('invoices.executions.table.executedAt'),
                    sortable: true,
                    valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) =>
                      params.row.executedAt ?? params.row.plannedFrom,
                    valueFormatter: (params: GridValueFormatterParams) =>
                      params.value ? t('global.date', { date: new Date(params.value) }) : '',
                    width: 150,
                  }),
                  COLUMN_TYPES.TEXT({
                    field: 'organisationUnit.name',
                    color: 'secondary',
                    valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) =>
                      params.row.clientOrganisationUnit.clientUnitName,
                    label: t('invoices.executions.table.clientUnitName'),
                    sortable: true,
                    minWidth: 200
                  }),
                  COLUMN_TYPES.TEXT({
                    field: 'supOrgUnit.name',
                    color: 'secondary',
                    valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) => params.row.supplierOrganisationUnit.supplierUnitName,
                    label: t('invoices.executions.table.supplierUnitName'),
                    sortable: true,
                    width: 200,
                  }),
                  COLUMN_TYPES.TEXT({
                    field: 'name',
                    color: 'secondary',
                    label: t('invoices.executions.table.name'),
                    minWidth: 250,
                  }),
                  COLUMN_TYPES.TEXT_BOLD({
                    field: 'serviceKind.kind',
                    label: t('invoices.executions.table.type'),
                    valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) => params.row.kind,
                    sortable: true,
                    width: 200,
                  }),
                  COLUMN_TYPES.TEXT({
                    field: 'waste.name',
                    color: 'secondary',
                    label: t('invoices.executions.table.waste'),
                    valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) => params.row.waste,
                    sortable: true,
                    width: 200,
                  }),
                  COLUMN_TYPES.TEXT({
                    field: 'container.name',
                    color: 'secondary',
                    label: t('invoices.executions.table.container'),
                    valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) => params.row.container,
                    sortable: true,
                    width: 150,
                  }),
                  COLUMN_TYPES.NUMBER({
                    field: 'executionLine.quantity',
                    label: t('invoices.executions.table.quantity'),
                    sortable: true,
                    valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) => params.row.quantity,
                    endAdornment: (row: ExecutionLine) => (<Typography fontSize={8}
                                                                       fontWeight="400">
                      {row?.unit}
                    </Typography>),
                    valueSetter: (params: GridValueSetterParams) => ({
                      ...params.row,
                      quantity: params.value ?? 0,
                      totalSell: (params.value ?? 0) * (params.row.puSell ?? 0) * 100,
                      totalBuy: (params.value ?? 0) * (params.row.puBuy ?? 0) * 100,
                    }),
                    width: 130,
                  }),
                  COLUMN_TYPES.NUMBER({
                    field: 'executionLine.puBuy',
                    label: t('invoices.executions.table.puBuy'),
                    valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) =>
                      params.row.puBuy ? NumberExt.round(params.row.puBuy / 100, 2) : undefined,
                    valueSetter: (params: GridValueSetterParams) => ({
                      ...params.row,
                      puBuy: (params.value ?? 0) * 100,
                      totalBuy: (params.value ?? 0) * (params.row.quantity ?? 0) * 100,
                    }),
                    width: 100,
                  }),
                  COLUMN_TYPES.NUMBER({
                    field: 'executionLine.puSell',
                    inputColor: (executionLine: ExecutionLine) =>
                      executionLine.puSell <= executionLine.puBuy ? Colors.error : undefined,
                    label: t('invoices.executions.table.puSell'),
                    valueGetter: (params: GridValueGetterParams<any, ExecutionLine>) =>
                      params.row.puSell ? NumberExt.round(params.row.puSell / 100, 2) : undefined,
                    valueSetter:  (params: GridValueSetterParams) => {
                      return {
                        ...params.row,
                        puSell: (params.value ?? 0) * 100,
                        totalSell: (params.value ?? 0) * (params.row.quantity ?? 0) * 100
                      };
                    },
                    width: 100,
                  }),
                  COLUMN_TYPES.TEXT({
                    field: 'totalBuy',
                    label: t('invoices.executions.table.totalBuy'),
                    valueFormatter: (params: GridValueFormatterParams) =>
                      params.value ? NumberExt.round(params.value / 100, 2) : '',
                    width: 100,
                  }),
                  COLUMN_TYPES.TEXT({
                    field: 'executionLine.supplierOrganisationUnit.currency',
                    label: '',
                    valueGetter: (params: GridValueGetterParams<ExecutionLine>) => params.row.supplierOrganisationUnit.currency,
                    width: 30,
                  }),
                  COLUMN_TYPES.TEXT({
                    field: 'totalSell',
                    label: t('invoices.executions.table.totalSell'),
                    valueFormatter: (params: GridValueFormatterParams) =>
                      params.value ? NumberExt.round(params.value / 100, 2) : '',
                    width: 100,
                  }),
                  COLUMN_TYPES.TEXT({
                    field: 'executionLine.clientOrganisationUnit.currency',
                    label: '',
                    valueGetter: (params: GridValueGetterParams<ExecutionLine>) => params.row.clientOrganisationUnit.currency,
                    width: 30,
                  }),
                  COLUMN_TYPES.ICONS({
                    field: 'properties',
                    label: '',
                    icons: [
                      {
                        icon: <Comment style={{ width: 18, marginTop: 5, height: 21 }} />,
                        hidden: (execution: Execution) => (execution.commentsCount ?? 0) <= 0,
                      },
                      {
                        icon: <Alert style={{ height: 18, width: 21 }} />,
                        hidden: (execution: Execution) => (execution.issueCount ?? 0) <= 0,
                      },
                    ],
                    width: 60,
                  }),
                ]}
                handlePageSizeChange={executionLineService.setPagination('limit')}
                pageSize={tablePagination.limit}
                handlePageChange={(page) => executionLineService.setPagination('page')(page + 1)}
                page={tablePagination.page - 1}
                handleSortModelChange={executionLineService.setSort}
                sortModel={[sortModel]}
                disableRowSelectionOnClick={true}
                handleRowSelected={executionLineService.setSelectedExecutionLines}
                rowsSelected={selectedExecutionLines}
                totalRows={executionLineQuery.totalRows}
                autoHeight={false}
                data={executionLines} />
              </Stack>
            </Stack>
          </Stack>
        );
      case 1:
        return (
          <Stack pt="20px" width="100%" direction="column" spacing={2} alignItems="center">
            <Stack direction="row" spacing={2}>
              <Stack spacing={0.5} flex={1}>
                <Typography fontSize="16px" fontWeight="400">
                  {t('invoices.bills.create.invoicingRule')}
                </Typography>
                <SelectComponent inputWidth={400} color="secondary" value={bill.invoicingRule} handleChange={(value) => updateBill('invoicingRule')(value)} items={FacturationRuleType.selectItems().filter((s) => [FacturationRuleType.CLIENT_UNIT_DISTINCT, FacturationRuleType.INVOICE_ADDRESS].includes(s.value))} />
              </Stack>
              <Stack spacing={0.5} flex={1}>
                <Typography fontSize="16px" fontWeight="400">
                  {t('invoices.bills.create.reminderDate')}
                </Typography>
                <DatePickerComponent
                  color="secondary"
                  value={bill.reminderDate}
                  handleChange={(reminderDate) => updateBill('reminderDate')(reminderDate)}
                  error={errors.reminderDate}/>
              </Stack>
            </Stack>
            <CheckboxComponent
              value={!!bill.includeAdminFee}
              handleChange={updateBill('includeAdminFee')}
              label={t('invoices.bills.create.includeAdminFee')}
            />
          </Stack>
        );
    }
  };

  const getStepAction = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Button disabled={selectedExecutionLines.length === 0} variant="contained" color="primary" onClick={() => setStep(1)}>
            {t('global.next')}
          </Button>
        );
      case 1:
        return (
          <LoadingButton loading={generateLoading} variant="contained" color="primary" disabled={checkIfErrors(errors)} onClick={handleGenerate}>
            {t('global.save')}
          </LoadingButton>
        );
    }
  };

  const errors = {
    documentDate: getFieldError(bill.documentDate, FieldValidationType.REQUIRED_DATE),
    reminderDate: getFieldError(bill.reminderDate, FieldValidationType.REQUIRED_DATE),
    invoicingRule: getFieldError(bill.invoicingRule, FieldValidationType.REQUIRED_STRING),
  };

  return (
    <ModalComponent
      handleClose={handleClose}
      customColor={Colors.background}
      title={t('invoices.bills.create.titleExecLines')}
      maxWidth={step === 0 ? false : 'lg'}
      content={
        getStepContent(step)
      }
      actions={(
        <Stack direction="row" spacing={2}>
          {step === 0 && <Button variant="outlined" color="primary" onClick={handleClose}>{t('global.cancel')}</Button>}
          {step !== 0 && <Button variant="outlined" color="primary"
                                 onClick={() => setStep(step - 1)}>{t('global.previous')}</Button>}
          {getStepAction(step)}
        </Stack>
      )}
      />
  );
};

export default GenerateBillFromExecLinesModal;
